import { nativeSymbol } from "../../../config/elysium";
import { useEffect, useState } from "react";
import { formatEther } from "viem";
import type { RedeemReviewView } from "../types";

export function RedeemReview({ review, busy, disabled, onCancel, onConfirm, onRefresh }: {
  review: RedeemReviewView; busy: boolean; disabled: boolean;
  onCancel(): void; onConfirm(): void; onRefresh(): void;
}) {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1_000));
  useEffect(() => {
    setNow(Math.floor(Date.now() / 1_000));
    const timer = setInterval(() => setNow(Math.floor(Date.now() / 1_000)), 1_000);
    return () => clearInterval(timer);
  }, [review.id]);
  const expired = now >= review.deadline;
  const withFees = review.route === "WITH_FEES";
  return <section className="redeem-review" aria-label="Review redemption">
    <h3>Burn this NFT and redeem?</h3>
    <dl className="detail-list mint-review-details">
      <div><dt>{withFees ? "Simulated payout after sweep" : "Estimated payout"}</dt><dd>{formatEther(BigInt(review.expectedPayoutWei))} {nativeSymbol(review.chainId)}<br />{review.expectedPayoutWei} wei</dd></div>
      {withFees ? <>
        <div><dt>Vault pool now</dt><dd>{review.vaultPoolWei ?? "Unavailable"} wei</dd></div>
        <div><dt>Estimated pool after sweep</dt><dd>{review.poolAfterSweepWei ?? "Unavailable"} wei</dd></div>
        {review.feeEstimatedPayoutWei ? <div><dt>Fee balance estimate</dt><dd>{review.feeEstimatedPayoutWei} wei</dd></div> : null}
        <div><dt>Quote block</dt><dd>{review.quoteBlockNumber} · {review.quoteBlockHash}</dd></div>
      </> : null}
      <div><dt>Minimum received</dt><dd>{formatEther(BigInt(review.minimumPayoutWei))} {nativeSymbol(review.chainId)}<br />{review.minimumPayoutWei} wei</dd></div>
      {withFees ? <div><dt>Minimum policy</dt><dd>Exactly the reviewed simulated payout. A smaller payout requires a new review.</dd></div> :
        <div><dt>Tolerance</dt><dd>1% · rounded down to whole wei</dd></div>}
      <div><dt>Quote expires</dt><dd>{new Date(review.deadline * 1_000).toLocaleString()} ({Math.max(0, review.deadline - now)}s remaining)</dd></div>
      <div><dt>Wallet</dt><dd>{review.account}</dd></div>
      <div><dt>Source token</dt><dd>{review.sourceToken}</dd></div>
      <div><dt>Chain</dt><dd>{review.chainId}</dd></div>
      <div><dt>Target contract</dt><dd>{review.collection}</dd></div>
      <div><dt>Token ID</dt><dd>{review.tokenId}</dd></div>
      <div><dt>{nativeSymbol(review.chainId)} value</dt><dd>0 {nativeSymbol(review.chainId)} + network gas</dd></div>
    </dl>
    {withFees && review.feeEstimatedPayoutWei ?
      <p role="status">The fee balance estimate differs from the contract simulation. Review the simulated payout and minimum above before confirming.</p> : null}
    <p>The quote lasts {withFees ? "600" : "300"} seconds. A successful redemption pays this NFT's share of the {withFees ? "source-token pool after the fee sweep" : "current source-token pool"} and permanently burns this NFT. A failed payout reverts the burn. The minimum and deadline shown here will not change during submission.</p>
    {expired ? <p role="status">Quote expired. Get a new preview and review its minimum before confirming.</p> : null}
    <div className="modal-actions"><button className="secondary-action" type="button" disabled={busy} onClick={onCancel}>Cancel redemption</button>
      <button className="primary-action" type="button" disabled={busy || disabled} onClick={expired ? onRefresh : onConfirm}>
        {busy ? "Checking redemption…" : expired ? "Prepare a new redemption quote" : "Confirm redemption in wallet"}
      </button></div>
  </section>;
}
